/**
 * In-editor AI assist for report profiles.
 * - Build/refine: admin-actions profile planner (applies to draft)
 * - Questions: Ask 5x insights chat with editor context
 */

import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { planAdminAction } from "../api/adminClient";
import { fetchInsightsChat } from "../api/analyticsClient";
import {
  ASSIGNMENT_SCOPES,
  defaultAssignmentPrefs,
} from "./ProfileAssignmentForm";

function isPureQuestion(text) {
  const raw = String(text || "").trim();
  const t = raw.toLowerCase();
  if (!t) return false;

  if (
    /\b(add|remove|enable|disable|rename|call it|name it|assign|reorder|move|put|build|create|make|update|change|set|include|exclude|without|skip)\b/.test(
      t
    )
  ) {
    return false;
  }

  if (/\?\s*$/.test(raw)) return true;

  if (/^(what|which|who|how|why|when|where|explain|tell me|describe|compare)\b/.test(t)) {
    return true;
  }

  if (/\b(what is|what's|what are|how do|how does|difference between|when should|can i)\b/.test(t)) {
    return true;
  }

  return false;
}

function applyPlanToEditor(plan, onApply, onAssignmentChange) {
  if (!plan || typeof plan !== "object") return false;
  const updates = {};

  if ((plan.name || "").trim()) updates.name = plan.name.trim();
  if (plan.description != null && String(plan.description).trim()) {
    updates.description = plan.description;
  }
  if ("target_role" in plan) updates.targetRole = plan.target_role || "";
  if (Array.isArray(plan.blocks) && plan.blocks.length) {
    updates.blocks = plan.blocks.map((b, i) => ({
      block_id: b.block_id || b.block_type,
      block_type: b.block_type || b.block_id,
      instance_id: b.instance_id || undefined,
      order: b.order ?? i + 1,
      enabled: b.enabled !== false,
      config: b.config && typeof b.config === "object" ? b.config : {},
      name: b.name,
    }));
  }

  if (Object.keys(updates).length) onApply(updates);
  if (onAssignmentChange) onAssignmentChange(defaultAssignmentPrefs(plan));
  return Object.keys(updates).length > 0;
}

function buildEditorContext({ name, description, targetRole, isTeamBase, blocks, catalog, isNew }) {
  const enabled = (blocks || []).filter((b) => b.enabled !== false);
  return {
    page: "report_profiles",
    scope: {
      label: "Report Profile Editor",
      text: "Admin is editing a draft in the profile builder — explain blocks, order, company vs team reports",
    },
    editorOpen: true,
    reportBase: isTeamBase ? "team" : "company",
    isNewProfile: !!isNew,
    editingProfile: {
      name: name || null,
      description: (description || "").slice(0, 300) || null,
      target_role: targetRole || null,
      block_count: enabled.length,
      block_ids: enabled.map((b) => b.block_id),
    },
    blockCatalog: (catalog || []).map((b) => ({
      block_id: b.block_id,
      name: b.name,
      description: (b.description || "").slice(0, 200),
      company_base_only: !!b.company_base_only,
      team_base_only: !!b.team_base_only,
    })),
    hints: [
      "Answer about block meanings, builder usage, company vs team report differences, and target roles.",
      "Only reference block_ids from blockCatalog. This is the current draft — not portfolio-wide stats.",
    ],
  };
}

function buildSuggestions({ isTeamBase, targetRole, name }) {
  const role = targetRole || "leadership";
  const base = isTeamBase ? "team" : "company";
  const items = [
    {
      id: "build_with_ai",
      label: `Build with AI for ${targetRole || base + " report"}`,
      message: targetRole
        ? `Build with AI a ${base} report profile for ${targetRole}: suggest profile name, description, and an ordered block list for this audience`
        : `Build with AI a ${base} report profile: suggest name, description, and blocks in a sensible order`,
      kind: "build",
    },
    {
      id: "write_description",
      label: "Write description with AI",
      message: `Refine this draft: write only a new description paragraph for a ${base} report${
        targetRole ? ` for ${targetRole}` : ""
      }. Keep all blocks exactly as they are${name ? `. Profile name: ${name}` : ""}`,
      kind: "build",
    },
  ];

  if (isTeamBase) {
    items.push({
      id: "team_rankings_pack",
      label: "Team pack: rankings + comments",
      message:
        "Build team report profile with pillar scores, team pillar rankings and comments, top priorities and gaps — no financial performance",
      kind: "build",
    });
    items.push({
      id: "explain_team_rankings",
      label: "What are team pillar rankings?",
      message: "What is the team pillar rankings block and when should I include it?",
      kind: "question",
    });
  } else {
    items.push({
      id: "board_pack",
      label: "Board pack (no role breakdown)",
      message:
        "Build company report profile for Board Member named Board Pack: intro, pillar scores, opportunity gaps, variance analysis — no individual breakdown or financials",
      kind: "build",
    });
    items.push({
      id: "explain_individual",
      label: "When use individual breakdown?",
      message: "When should I include the individual breakdown block in a company report profile?",
      kind: "question",
    });
  }

  items.push({
    id: "explain_blocks",
    label: "Explain current blocks",
    message: "Explain what each block in my current draft contains and who typically reads it",
    kind: "question",
  });

  items.push({
    id: "priorities_pointers",
    label: "Priorities: add AI pointers",
    message:
      "Under Top Priorities, I want 3 specific actionable pointers based on employee feedback, with evidence shown",
    kind: "build",
  });

  items.push({
    id: "reorder_variance",
    label: "Move variance before gaps",
    message: "Put variance analysis before opportunity gaps in the block order",
    kind: "build",
  });

  if (!isTeamBase) {
    items.push({
      id: "remove_financials",
      label: "Remove financial section",
      message: "Remove financial performance from this profile and keep the rest",
      kind: "build",
    });
  }

  items.push({
    id: "team_vs_company",
    label: "Team vs company reports?",
    message: "What is the difference between team and company report profiles and their blocks?",
    kind: "question",
  });

  return items;
}

export default function ProfileBuilderAssist({
  name,
  description,
  targetRole,
  isTeamBase,
  blocks,
  catalog,
  isNew = false,
  onApply,
  disabled = false,
  onAssignmentChange,
  assignmentPrefs = null,
}) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [replyKind, setReplyKind] = useState(null);
  const [usedSuggestionIds, setUsedSuggestionIds] = useState([]);
  const [chatSessionId, setChatSessionId] = useState("");

  const allSuggestions = useMemo(
    () => buildSuggestions({ isTeamBase, targetRole, name }),
    [isTeamBase, targetRole, name]
  );

  const visibleSuggestions = useMemo(
    () => allSuggestions.filter((s) => !usedSuggestionIds.includes(s.id)),
    [allSuggestions, usedSuggestionIds]
  );

  const markSuggestionUsed = (suggestionId) => {
    if (!suggestionId) return;
    setUsedSuggestionIds((prev) =>
      prev.includes(suggestionId) ? prev : [...prev, suggestionId]
    );
  };

  const buildDraft = () => {
    const draft = {
      refine: true,
      name: name || "",
      description: description || "",
      target_role: targetRole || null,
      is_team_base: isTeamBase,
      is_company_base: !isTeamBase,
      blocks: blocks || [],
    };
    const prefs = assignmentPrefs || defaultAssignmentPrefs();
    if (prefs.companyScope === ASSIGNMENT_SCOPES.ONE && (prefs.companyName || "").trim()) {
      draft.assign_to_company = prefs.companyName.trim();
      draft.assign_to_all_companies = false;
    } else {
      draft.assign_to_all_companies = true;
    }
    return draft;
  };

  const runQuestion = async (text) => {
    const context = buildEditorContext({
      name,
      description,
      targetRole,
      isTeamBase,
      blocks,
      catalog,
      isNew,
    });
    const res = await fetchInsightsChat({
      message: text,
      context,
      sessionId: chatSessionId || null,
    });
    const sid = res?.data?.session_id || "";
    if (sid) setChatSessionId(sid);
    setReply(res?.data?.reply || "No answer available.");
    setReplyKind("answer");
  };

  const runBuild = async (text) => {
    const res = await planAdminAction({
      message: text,
      draft: buildDraft(),
    });
    const data = res?.data || {};

    if (
      (data.action === "create_report_profile" || data.action === "refine_report_profile") &&
      data.plan
    ) {
      applyPlanToEditor(data.plan, onApply, onAssignmentChange);
      setReply(
        data.reply ||
          (data.action === "refine_report_profile"
            ? "Updated block settings in the editor. Review and save when ready."
            : "Applied to the editor. Review blocks and assignment, then save when ready.")
      );
      setReplyKind("applied");
      setInput("");
      return;
    }

    if (data.action === "clarify") {
      if (data.draft) applyPlanToEditor(data.draft, onApply, onAssignmentChange);
      setReply(
        data.reply ||
          "Applied what I could — add a profile name or block list if anything is still missing."
      );
      setReplyKind(data.draft ? "applied" : "clarify");
      return;
    }

    setReply(
      data.reply ||
        "Describe name, role, blocks, or refine a block — e.g. “Under Top Priorities, add 3 actionable pointers with evidence”."
    );
    setReplyKind("info");
  };

  const runSubmit = async (message, suggestionId = null) => {
    const text = (message || "").trim();
    if (!text || loading || disabled) return;

    markSuggestionUsed(suggestionId);
    setLoading(true);
    setReply("");
    setReplyKind(null);

    try {
      const suggestion = suggestionId
        ? allSuggestions.find((s) => s.id === suggestionId)
        : null;
      const asQuestion =
        suggestion?.kind === "question" || (!suggestionId && isPureQuestion(text));

      if (asQuestion) {
        await runQuestion(text);
      } else {
        await runBuild(text);
      }
    } catch (err) {
      toast.error(err?.message || "Could not run profile assist");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-[#004f73]/20 bg-gradient-to-b from-[#004f73]/[0.04] to-white p-3 space-y-2.5 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-[#004f73] shrink-0">
            <path d="M11.3 1.046a1 1 0 011.414 0l1.172 1.172a1 1 0 010 1.414l-.707.707 1.414 1.414.707-.707a1 1 0 011.414 0l1.172 1.172a1 1 0 010 1.414l-1.172 1.172a1 1 0 01-1.414 0l-.707-.707-1.414 1.414.707.707a1 1 0 01-1.414 0l-1.172-1.172a1 1 0 010-1.414l.707-.707-1.414-1.414-.707.707a1 1 0 01-1.414 0L9.13 4.818a1 1 0 010-1.414l1.172-1.172zM7 11a1 1 0 011-1h4a1 1 0 110 2H8a1 1 0 01-1-1z" />
          </svg>
          <p className="text-xs font-semibold text-[#004f73]">AI profile assist</p>
        </div>
        <span className="text-[10px] text-gray-400 shrink-0">
          {isTeamBase ? "Team" : "Company"} draft
        </span>
      </div>

      <p className="text-[11px] text-gray-500 leading-snug">
        Build or refine the recipe — name, blocks, order, or “Under Top Priorities, add
        actionable pointers.” AI pointers are generated at PDF time from live data, not
        stored in the profile.
      </p>

      {visibleSuggestions.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {visibleSuggestions.map((chip) => (
            <button
              key={chip.id}
              type="button"
              disabled={disabled || loading}
              onClick={() => runSubmit(chip.message, chip.id)}
              className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors disabled:opacity-50 ${
                chip.kind === "build"
                  ? "border-[#004f73]/35 text-[#004f73] bg-white hover:bg-[#004f73]/8"
                  : "border-slate-200 text-slate-600 bg-slate-50 hover:bg-slate-100"
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-gray-400 italic">All quick suggestions used — type below.</p>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          disabled={disabled || loading}
          onChange={(e) => setInput(e.target.value)}
          placeholder='e.g. Name it Board Pack for Board Member, assign to Acme Corp, intro + pillars + gaps'
          className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#004f73]/30"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              runSubmit(input);
            }
          }}
        />
        <button
          type="button"
          disabled={disabled || loading || !input.trim()}
          onClick={() => runSubmit(input)}
          className="shrink-0 text-xs font-medium text-white bg-[#004f73] rounded-lg px-3 py-2 hover:bg-[#003d5c] disabled:opacity-50"
        >
          {loading ? "…" : "Go"}
        </button>
      </div>

      {reply ? (
        <div
          className={`text-[11px] leading-relaxed border-t pt-2 whitespace-pre-wrap ${
            replyKind === "answer"
              ? "text-slate-700 bg-slate-50/80 rounded-lg px-2.5 py-2 border-slate-100"
              : "text-gray-600 border-[#004f73]/10"
          }`}
        >
          {replyKind === "answer" ? (
            <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
              Answer
            </span>
          ) : null}
          {reply}
        </div>
      ) : null}
    </div>
  );
}
